import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import config from 'constants/apiConfig';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { selectGroupGuid } from 'slices/commonSelectors';

export type HwcgMemberCompany = {
  id: string;
  groupGuid: string;
  companyName: string;
  eocLocation: string;
  officeLocation: string;
  createdAt: string;
  updatedAt: string;
};

export const hwcgMemberCompanyKeys = {
  list: (groupGuid: string) => ['hwcg-member-companies', groupGuid] as const,
};

export function useHwcgMemberCompanies(options?: {
  enabled?: boolean;
  groupGuid?: string;
}) {
  const selectedGroupGuid = useSelector(selectGroupGuid);
  const groupGuid = options?.groupGuid || selectedGroupGuid || '';
  return useQuery({
    queryKey: hwcgMemberCompanyKeys.list(groupGuid),
    enabled: Boolean(groupGuid) && options?.enabled !== false,
    queryFn: async () => {
      const response = await axios.get<HwcgMemberCompany[]>(
        config.apiGateway.hwcgMemberCompanies,
        { params: { groupGuid } }
      );
      return response.data ?? [];
    },
  });
}

export function useUpdateHwcgMemberCompany() {
  const groupGuid = useSelector(selectGroupGuid) ?? '';
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      eocLocation?: string;
      officeLocation?: string;
    }) => {
      const response = await axios.put<HwcgMemberCompany>(
        `${config.apiGateway.hwcgMemberCompanies}/${input.id}`,
        {
          groupGuid,
          eocLocation: input.eocLocation,
          officeLocation: input.officeLocation,
        }
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: hwcgMemberCompanyKeys.list(groupGuid),
      });
    },
    onError: () => {
      toast.error('Could not save the member company locations');
    },
  });
}

function locationForCompany(
  companies: ReadonlyArray<{
    companyName: string;
    eocLocation?: string;
    officeLocation?: string;
  }>,
  companyName: string | null | undefined,
  field: 'eocLocation' | 'officeLocation'
): string | null {
  const key = companyName?.trim().toLowerCase() ?? '';
  if (!key) return null;
  const match = companies.find(
    (row) => row.companyName.trim().toLowerCase() === key
  );
  const location = match?.[field]?.trim() ?? '';
  return location || null;
}

/** Case-insensitive lookup of a company's directory EOC location. */
export function eocLocationForCompany(
  companies: ReadonlyArray<{ companyName: string; eocLocation: string }>,
  companyName: string | null | undefined
): string | null {
  return locationForCompany(companies, companyName, 'eocLocation');
}

/** Case-insensitive lookup of a company's directory office / report-to location. */
export function officeLocationForCompany(
  companies: ReadonlyArray<{ companyName: string; officeLocation: string }>,
  companyName: string | null | undefined
): string | null {
  return locationForCompany(companies, companyName, 'officeLocation');
}
